import { useEffect } from 'react';

const INSET_PROPERTIES = ['--backdrop-inset-top', '--backdrop-inset-left', '--backdrop-inset-right'];

/**
 * Keeps the modal backdrop on the main content: writes the header height, the left sidebar width
 * and the docked AI chat width to `:root` as the `--backdrop-inset-*` variables.
 *
 * The page never scrolls, so the main content box gives the top and left insets as they are. The
 * right inset is the AI chat dock alone: a panel in the page's own right slot stays covered. The
 * insets follow a resize of either box — the sidebar or the chat opened or closed — and of the
 * window. Removed on unmount, so a screen outside the page layout gets a full-window backdrop.
 */
const useBackdropInsets = (main: HTMLElement | null, aiChatDock: HTMLElement | null): void => {
  useEffect(() => {
    const root = document.documentElement;

    const update = () => {
      const mainBox = main?.getBoundingClientRect();

      root.style.setProperty('--backdrop-inset-top', `${mainBox?.top ?? 0}px`);
      root.style.setProperty('--backdrop-inset-left', `${mainBox?.left ?? 0}px`);
      root.style.setProperty('--backdrop-inset-right', `${aiChatDock?.offsetWidth ?? 0}px`);
    };

    update();

    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);

    [main, aiChatDock].forEach((element) => {
      if (element) {
        observer?.observe(element);
      }
    });
    window.addEventListener('resize', update);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      INSET_PROPERTIES.forEach((property) => root.style.removeProperty(property));
    };
  }, [aiChatDock, main]);
};

export default useBackdropInsets;
